import { supabase, isSupabaseConfigured } from '../config/supabase';
import { uploadRestaurantLogo, deleteRestaurantLogo } from './storage';

/**
 * Get restaurant information
 * @returns {Promise<Object>} Restaurant info object
 */
export const getRestaurantInfo = async () => {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('Supabase not configured');
    return { data: null, error: { message: 'Supabase not configured' } };
  }

  try {
    const { data, error } = await supabase
      .from('restaurant_info')
      .select('*')
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error fetching restaurant info:', error);
      return { data: null, error };
    }

    return { data: data || null, error: null };
  } catch (error) {
    console.error('Error in getRestaurantInfo:', error);
    return { data: null, error };
  }
};

/**
 * Save or update restaurant information
 * @param {Object} formData - Restaurant information object
 * @param {File|null} logoFile - Logo file to upload (optional)
 * @returns {Promise<Object>} Saved restaurant info
 */
export const saveRestaurantInfo = async (formData, logoFile = null) => {
  if (!isSupabaseConfigured() || !supabase) {
    console.error('Supabase not configured - cannot save data');
    return { data: null, error: { message: 'Supabase not configured' } };
  }

  // Validate required fields
  if (!formData.name || !formData.name.trim()) {
    return { data: null, error: { message: 'Restaurant name is required' } };
  }
  if (!formData.phone || !formData.phone.trim()) {
    return { data: null, error: { message: 'Phone number is required' } };
  }
  if (!formData.email || !formData.email.trim()) {
    return { data: null, error: { message: 'Email address is required' } };
  }
  if (!formData.address || !formData.address.trim()) {
    return { data: null, error: { message: 'Address is required' } };
  }

  try {
    console.log('Starting to save restaurant info to Supabase...');
    
    // Get current logo URL from database to check if we need to delete old one
    const { data: existingData } = await supabase
      .from('restaurant_info')
      .select('logo_url')
      .limit(1)
      .single();
    
    const existingLogoUrl = existingData?.logo_url;
    let logoUrl = formData.logo; // Keep existing logo URL if no new file and not removed

    // Handle logo removal or upload
    if (logoFile) {
      // Upload new logo
      console.log('Uploading new logo to Supabase Storage...');
      // Delete old logo if exists
      if (existingLogoUrl && existingLogoUrl.startsWith('http')) {
        try {
          await deleteRestaurantLogo(existingLogoUrl);
          console.log('Old logo deleted successfully');
        } catch (error) {
          console.warn('Could not delete old logo:', error);
          // Continue even if deletion fails
        }
      }

      // Upload new logo
      logoUrl = await uploadRestaurantLogo(logoFile);
      console.log('Logo uploaded successfully:', logoUrl);
    } else if (formData.logo === null || formData.logo === '') {
      // Logo was removed - delete from storage if it exists
      if (existingLogoUrl && existingLogoUrl.startsWith('http')) {
        console.log('Removing logo from Supabase Storage...');
        try {
          await deleteRestaurantLogo(existingLogoUrl);
          console.log('Logo deleted successfully');
        } catch (error) {
          console.warn('Could not delete logo from storage:', error);
          // Continue even if deletion fails
        }
      }
      logoUrl = null;
    }

    // Prepare data for database
    const restaurantData = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      address: formData.address.trim(),
      description: formData.description ? formData.description.trim() : null,
      logo_url: logoUrl || null
    };

    console.log('Data to save:', restaurantData);

    // Check if restaurant info already exists
    const { data: existing, error: checkError } = await supabase
      .from('restaurant_info')
      .select('id')
      .limit(1)
      .single();

    let result;
    let isUpdate = false;

    // If no existing record or error (PGRST116 means no rows), insert new
    if (!existing || checkError?.code === 'PGRST116') {
      console.log('No existing record found, inserting new restaurant info...');
      // Insert new record
      const { data, error } = await supabase
        .from('restaurant_info')
        .insert([restaurantData])
        .select()
        .single();

      if (error) {
        console.error('Error inserting restaurant info:', error);
        throw error;
      }
      result = data;
      console.log('✅ Restaurant info inserted successfully:', result);
    } else {
      console.log('Existing record found, updating restaurant info...', existing.id);
      isUpdate = true;
      // Update existing record
      const { data, error } = await supabase
        .from('restaurant_info')
        .update(restaurantData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating restaurant info:', error);
        throw error;
      }
      result = data;
      console.log('✅ Restaurant info updated successfully:', result);
    }

    // Verify the data was saved by fetching it back
    const { data: verifyData, error: verifyError } = await supabase
      .from('restaurant_info')
      .select('*')
      .eq('id', result.id)
      .single();

    if (verifyError) {
      console.warn('Could not verify saved data:', verifyError);
    } else {
      console.log('✅ Verified: Data saved correctly in Supabase:', verifyData);
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('❌ Error in saveRestaurantInfo:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint
    });
    return { data: null, error };
  }
};

